import { Api } from './BaseApi'
import { ITicket } from "../../types/Ticket/ITicket";
import { IType } from '../../types/IType';
import { ISLAConfig } from '../../types/ISLAConfig';
import { IStatus } from '../../types/Ticket/Status/IStatus';
import { IQuickNoteCategory } from '../../types/Ticket/IQuickNotes';
import { ITemplate, ITemplateAction, ITemplateAssignment, ITemplateListItem } from '../../types/Ticket/ITemplate';
import { IAction, IActionDTO, IActionIds } from '../../types/Action/IAction';
import { IStopwatch, StopwatchProps } from '../../types/Ticket/IStopwatch';
import { IStatusType } from '../../types/Ticket/Status/IStatusListItem';
import { ITicketConfig } from '../../types/Ticket/ITicketConfig';
import IForm, { IField } from '../../types/Form/IForm';
import { IShare } from '../../types/Ticket/IShare';
import { ReassignProps } from '../../types/Props/ReassignProps';
import { IEmailAction } from '../../types/Action/IEmailAction';
import { IStaff } from '../../types/IStaff';
import { IStateHistory } from '../../types/Ticket/IStateHistory';
import { ISetStatusProps } from '../../types/Ticket/Status/ISetStatusProps';
import { IPriority } from '../../types/Priority/IPriority';
import { ISetPriorityProps } from '../../types/Priority/ISetPriorityProps';
import { IHierarchy } from '../../types/Ticket/IHierarchy';
import { ITicketAsset } from '../../types/Ticket/ITicketAsset';
import { ITicketMergeProps } from '../../types/Ticket/ITicketMergeProps';
import { TicketSearchProps } from '../../types/Ticket/TicketSearchProps';

const TicketApi = Api.injectEndpoints({
  endpoints: (build) => ({
    getTicketForm: build.query<IForm, number>({
      query: (id) => `Ticket/${id}/Form`,
      providesTags: ["GetTicketForm"],
    }),
    readTicket: build.mutation<boolean, { ticketId: number }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId || 0}/Read/`,
        method: "POST",
        body: {},
      }),
    }),
    updateTicketHierarchy: build.mutation<any, { ticketId: number, info: IHierarchy }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId || 0}/Hierarchy/`,
        method: "POST",
        body: props.info,
      }),
      invalidatesTags: ["GetTicketDetails", "GetTicket", "GetChildTickets", "GetOpenParentTickets", "GetActions"]
    }),
    removeTicketHierarchy: build.mutation<any, { ticketId: number }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId || 0}/Hierarchy/`,
        method: "DELETE",
        body: {}
      }),
      invalidatesTags: ["GetTicketDetails", "GetTicket", "GetChildTickets", "GetOpenParentTickets", "GetActions"]
    }),
    updateTicketForm: build.mutation<any, { ticketId: number, formId: number, fields: IField[] }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId || 0}/Form/${props.formId}`,
        method: "PUT",
        body: props.fields,
      }),
      invalidatesTags: ["GetTicketForm", "GetTicketDetails", "GetTicket"]
    }),
    updateAction: build.mutation<any, { ticketId: number, Id: number, action: IActionDTO }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/Action/${props.Id}`,
        method: "PUT",
        body: props.action,
      }),
      invalidatesTags: ["GetActions", "GetAction", "GameplanAllocation", "TicketWorkstream"]
    }),
    updateType: build.mutation<any, { ticketId: number, typeId: number }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/Type`,
        method: "POST",
        body: { Type_Id: props.typeId, Overwrite: true },
      }),
      invalidatesTags: ["GetTicket", "GetTicketDetails"]
    }),
    updateSLA: build.mutation<any, { ticketId: number, slaID: number }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/SLA`,
        method: "POST",
        body: { SLA_Id: props.slaID },
      }),
      invalidatesTags: ["GetTicket", "GetTicketDetails", "ImageTicketTimeline"]
    }),
    shareTicket: build.mutation<any, { ticketId: number, share: IShare }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/Share`,
        method: "POST",
        body: props.share,
      }),
      invalidatesTags: ["GetActions"]
    }),
    getTicketConfig: build.query<ITicketConfig, number>({
      query: (id) => `Ticket/${id}/Details/Config`,
      providesTags: ["GetTicketConfig"],
    }),
    getTypeList: build.query<IType[], number | null>({
      query: (parentId) => "Ticket/Type?parentTypeId=" + parentId,
      providesTags: ["TypeList"],
    }),
    getMyTypeList: build.query<IType[], number | null>({
      query: (parentId) => "Ticket/Type/My?parentTypeId=" + parentId,
      providesTags: ["MyTypeList"],
    }),
    getSLAConfig: build.query<ISLAConfig, void>({
      query: () => "Ticket/SLAConfig",
      providesTags: ["SLAConfig"],
    }),
    getTicket: build.query<ITicket, { ticketId: number, populateFormData?: boolean, populateActions?: boolean }>({
      query: ({ ticketId, populateFormData, populateActions }) => `Ticket/${ticketId}/?populateFormData=${populateFormData || 'false'}&populateActions=${populateActions || 'false'}`,
      providesTags: ["GetTicket"],
    }),
    getTicketDetails: build.query<ITicket, { ticketId: number }>({
      query: ({ ticketId }) => `Ticket/${ticketId}/Details`,
      providesTags: ["GetTicketDetails"],
    }),
    getActions: build.query<IPagedResult<IAction>, {
      ticketId: number, skip: number, take: number, showSystemActions?: boolean, searchTerm?: string, searchTermOption?: string, authorIds?: number[], authorAndOr?: 'and' | 'or',
      actionTypeIds?: number[], actionTypeAndOr?: 'and' | 'or', dateRange?: string, hasReactions?: boolean, customStartDate?: Date, customEndDate?: Date
    }>({
      query: ({ ticketId, skip, take, showSystemActions, searchTerm, searchTermOption, authorIds, authorAndOr, actionTypeIds, actionTypeAndOr, hasReactions, dateRange, customStartDate, customEndDate }) => `Ticket/${ticketId}/Action?skip=${skip}&take=${take}&includeSystemActions=${showSystemActions == null ? 'true' : showSystemActions ? 'true' : 'false'}
&searchTerm=${encodeURIComponent(searchTerm || '')}&searchTermOption=${searchTermOption === 'Include' ? '1' : '0'}&authorIds=${authorIds?.filter(a => !isNaN(a)).map(a => a.toString()).join(',') || ''}&authorOption=${authorAndOr === 'or' ? '1' : '0'}&actionTypeIds=${actionTypeIds?.filter(a => !isNaN(a)).map(a => a.toString()).join(',') || ''}&actionTypeOption=${actionTypeAndOr === 'or' ? '1' : '0'}&hasReactions=${hasReactions ? 'true' : 'false'}&dateRange=${dateRange || ''}&startDate=${customStartDate?.toISOString() || ''}&endDate=${customEndDate?.toISOString() || ''}`,
      providesTags: ["GetActions"],
    }),
    getAction: build.query<IAction, { ticketId: number, actionId: number }>({
      query: ({ ticketId, actionId }) => `Ticket/${ticketId}/Action/${actionId}`,
      providesTags: ["GetAction"],
    }),
    getActionTypes: build.query<any[], void>({
      query: () => `Ticket/Action/Type`,
    }),
    getTicketActionTypes: build.query<any[], number>({
      query: (ticketId) => `Ticket/${ticketId}/Action/Type`,
      providesTags: ["ActionTypes"],
    }),
    getTicketAuthors: build.query<IStaff[], number>({
      query: (ticketId) => `Ticket/${ticketId}/Action/Author`,
      providesTags: ["ActionAuthors"],
    }),
    getTicketEmailTemplates: build.query<ITemplateListItem[], void>({
      query: (ticketId) => `Ticket/Action/Template`,
    }),
    getOpenTickets: build.query<ITicket[], { companyId?: number }>({
      query: ({ companyId }) => `/Ticket/?companyId=${companyId || 0}`,
      providesTags: ["GetOpenTickets"],
    }),
    getStatuses: build.query<IStatus[], void>({
      query: () => "Ticket/Status?statusTypeId=-1",
      providesTags: ["GetStatuses"],
    }),
    getMyStatuses: build.query<IStatusType[], void>({
      query: () => "Ticket/Status/My",
      providesTags: ["GetMyStatuses"],
    }),
    getStatus: build.query<IStatus, number>({
      query: (Id) => `Ticket/Status/${Id}`,
      providesTags: ["GetStatus"],
    }),
    getStateHistory: build.query<IStateHistory[], number>({
      query: (Id) => `Ticket/${Id}/State/History`,
      providesTags: ["GetStateHistory"],
    }),
    getOpenEndUserTickets: build.query<ITicket[], string>({
      query: (Uid) => `Ticket/ByEndUser/${Uid}`,
      providesTags: ["GetOpenTickets"],
    }),
    getOpenParentTickets: build.query<ITicket[], void>({
      query: () => "Ticket/Parent/",
      providesTags: ["GetOpenParentTickets"],
    }),
    getChildTickets: build.query<ITicket[], number>({
      query: (iticketId) => `Ticket/${iticketId}/Child/`,
      providesTags: ["GetChildTickets"],
    }),
    getQuickNotes: build.query<IQuickNoteCategory[], void>({
      query: () => "QuickNote/My/",
      providesTags: ["GetQuickNotes"],
    }),
    getQuickNoteAsAction: build.query<IAction, { id: number, ticketId: number }>({
      query: ({ id, ticketId }) => `QuickNote/${id}/AsAction/${ticketId}`,
      providesTags: ["GetQuickNoteAsAction"],
    }),
    getMyTemplates: build.query<ITemplateListItem[], void>({
      query: () => "Ticket/Template/My",
      providesTags: ["GetMyTemplates"],
    }),
    getTemplateActions: build.query<ITemplateAction[], number>({
      query: (id) => `Ticket/Template/${id}/Actions`,
      providesTags: ["GetTemplateActions"],
    }),
    getTemplateAssignment: build.query<ITemplateAssignment[], number>({
      query: (id) => `Ticket/Template/${id}/Assignment`,
      providesTags: ["GetTemplateAssignment"],
    }),
    getTemplate: build.query<ITemplate, number>({
      query: (id) => `Ticket/Template/${id}`,
      providesTags: ["GetTemplate"],
    }),
    getStopwatches: build.query<IStopwatch[], number>({
      query: (id) => `Ticket/${id || 0}/Stopwatch`,
      providesTags: ["GetStopwatches"],
    }),
    getPriorityList: build.query<IPriority[], number>({
      query: (prioritySetId) =>
        "Ticket/Priority/?prioritySetId=" + prioritySetId,
      providesTags: ["PriorityList"],
    }),
    setPriority: build.mutation<any | void, ISetPriorityProps>({
      query: (props) => ({
        url: `Ticket/${props.Ticket_Id}/Priority`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["GetTicketDetails", "GetTicket", "GetActions", "ImageTicketTimeline"]
    }),
    setStatus: build.mutation<any | void, ISetStatusProps>({
      query: (props) => ({
        url: `Ticket/${props.Ticket_Id}/Status`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["GetTicketDetails", "GetTicket", "GetActions", "ImageTicketTimeline", "Pins"]
    }),
    reassign: build.mutation<any | void, ReassignProps>({
      query: (props) => ({
        url: `Ticket/${props.TicketId}/Reassign`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["GetTicketDetails", "GetTicket", "GetActions"]
    }),
    startStopwatch: build.mutation<IStopwatch, StopwatchProps>({
      query: (props) => ({
        url: `Ticket/${props.TicketId || 0}/Stopwatch/Start`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["GetStopwatches"]
    }),
    stopStopwatch: build.mutation<IStopwatch | void, StopwatchProps>({
      query: (props) => ({
        url: `Ticket/${props.TicketId || 0}/Stopwatch/Stop`,
        method: "POST",
        body: props,
      }),
      invalidatesTags: ["GetStopwatches"]
    }),
    createAction: build.mutation<unknown, IActionDTO>({
      query: (info) => ({
        url: `Ticket/${info.Call_Id}/Action`,
        method: "POST",
        body: info,
      }),
      invalidatesTags: ["GetStopwatches", "GetTicket", "GetTicketDetails", "GetActions", "ActionTypes", "ActionAuthors", "GetFiles", "GetTicketFiles", "ImageTicketTimeline",
        "GameplanAllocation", "TicketWorkstream"
      ]
    }),
    UpdateActionPin: build.mutation<unknown, { Id: number, TicketId: number, Pinned: boolean }>({
      query: (info) => ({
        url: `Ticket/${info.TicketId}/Action/${info.Id}/Pin`,
        method: "POST",
        body: info,
      }),
      invalidatesTags: ["GetActions", "GetAction"]
    }),
    deleteAction: build.mutation<any, IActionDTO>({
      query: (action) => ({
        url: `Ticket/${action.Call_Id}/Action/${action.ID}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['GetActions', "GetFiles", "GetTicketFiles", "GetTicketDetails", "GameplanAllocation", "TicketWorkstream"],
    }),
    getEmailConfig: build.query<IEmailAction, number>({
      query: (id) => `Ticket/${id}/Email`,
    }),
    sendEmail: build.mutation<unknown, { ticketId: number, info: IEmailAction }>({
      query: ({ ticketId, info }) => ({
        url: `Ticket/${ticketId}/Email`,
        method: "POST",
        body: info,
      }),
      invalidatesTags: ["GetTicket", "GetTicketDetails", "GetActions", "GetTicketFiles", "GetFiles", "GameplanAllocation", "TicketWorkstream"]
    }),
    createTicket: build.mutation<unknown, ITicket>({
      query: (info) => ({
        url: "Ticket",
        method: "POST",
        body: info,
      }),
      invalidatesTags: ["GetStopwatches", "GetTicket", "GetTicketDetails", "GetFiles", "GetChildTickets", "GetOpenParentTickets"]
    }),
    bulkLogTicket: build.mutation<unknown, ITicket>({
      query: (info) => ({
        url: "Ticket/BulkLog",
        method: "POST",
        body: info,
      }),
      invalidatesTags: ["GetStopwatches", "GetTicket", "GetTicketDetails", "GetFiles"]
    }),
    actionsById: build.mutation<IAction[], IActionIds>({
      query: (info) => ({
        url: "Ticket/Action/ByIds",
        method: "POST",
        body: info,
      })
    }),
    refetchQuicknotes: build.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ['GetQuickNotes'],
    }),
    resendRatingEmail: build.mutation<null, number>({
      query: (id) => ({
        url: `Ticket/${id}/Rating/Send`,
        method: "POST"
      }),
      invalidatesTags: ["GetTicket", "GetTicketDetails"],
    }),
    getLinkedAssets: build.query<ITicketAsset[], number>({
      query: (id) => `Ticket/${id}/Asset`,
      providesTags: ["GetLinkedAssets"],
    }),
    saveLinkedAssets: build.mutation<null, { ticketId: number, assets: ITicketAsset[], append: boolean }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/Asset`,
        method: "POST",
        body: { Assets: props.assets, Append: props.append }
      }),
      invalidatesTags: ["GetLinkedAssets"],
    }),
    saveAssociatedContacts: build.mutation<null, { ticketId: number, contactIds: number[] }>({
      query: (props) => ({
        url: `Ticket/${props.ticketId}/Contact`,
        method: "POST",
        body: props.contactIds
      }),
      invalidatesTags: ["GetTicketDetails", "GetActions"],
    }),
    mergeActions: build.mutation<null, ITicketMergeProps>({
      query: (props) => ({
        url: `Ticket/${props.Ticket_Id}/Merge`,
        method: "POST",
        body: props
      }),
      invalidatesTags: ["GetTicketDetails", "GetActions"],
    }),
    searchTickets: build.mutation<ITicket[], TicketSearchProps>({
      query: (props) => ({
        url: `Ticket/Search`,
        method: "POST",
        body: props
      }),
    }),
  }),
  overrideExisting: false,
})

export { TicketApi };